import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  AppBar,
  Toolbar,
  TextField,
  Button,
  Stack,
  Divider,  
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Description as DescriptionIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import COLORS from '../../Utils/colors';
import { format } from 'date-fns/format';
import PaymentButton from '../../Components/PaymentButton';
import { request } from '../../Utils/request';
import { paiseToRupees } from '../../Utils/currencyConverter';

interface Guest {
  name: string;
  age: string;
  gender: 'male' | 'female' | '';
  contact: string;
  // email?: string;
}

interface BatchOption {
  label: string;
  id: string;
  batches?: any;
}

const Checkout: React.FC = () => {
  const MAX_GUESTS = 5;
  const navigate = useNavigate();
  const location = useLocation();
  const tripData = location.state?.tripData;
  const Type = location.state?.type;
  console.log('1234',Type)

  const [guests, setGuests] = useState<Guest[]>([
    { name: '', age: '', gender: '', contact: '' }
  ]);
  const [batchId, setBatchId] = useState<BatchOption | null>(null);
  const [showPriceDetails, setShowPriceDetails] = useState(false);

  const getAvailableSlots = () => {
    if (Type !== 'trip') return MAX_GUESTS;

    const selectedBatch = batchId?.batches;
    if (!selectedBatch) return 0;

    const groupSize = Number(selectedBatch.groupSize) || 0;
    const maxGroupSize = Number(selectedBatch.maxGroupSizeAllowed) || 0;
    const remainingSlots = Math.max(maxGroupSize - groupSize, 0);

    return Math.min(MAX_GUESTS, remainingSlots);
  };

  const handleBack = () => {
    window.history.back();
  };

  const confirmBooking = async (razorpay_payment_id: string) => {
    if (guests.length > MAX_GUESTS) {
      window.dispatchEvent(
        new CustomEvent('app-toast', {
          detail: {
            message: `Maximum ${MAX_GUESTS} guests can be added`,
            type: 'error',
          },
        }),
      );
      return;
    }

    // Only require batchId for trip bookings, not for itinerary
    if (Type === 'trip' && !batchId) {
      console.error("No batches selected");
      window.dispatchEvent(
        new CustomEvent('app-toast', {
          detail: {
            message: 'Please select a trip date to continue',
            type: 'error',
          },
        }),
      );
      return;
    }

    if (Type === 'trip') {
      const maxGuestsAllowed = getAvailableSlots();
      if (guests.length > maxGuestsAllowed) {
        window.dispatchEvent(
          new CustomEvent('app-toast', {
            detail: {
              message:
                maxGuestsAllowed > 0
                  ? `Only ${maxGuestsAllowed} guest${maxGuestsAllowed > 1 ? 's are' : ' is'} available for this date`
                  : 'No slots available for this date',
              type: 'error',
            },
          }),
        );
        return;
      }
    }
    
    try {
      // Determine the endpoint based on booking type
      const endpoint = Type === 'itinerary' 
        ? `/bookings/itineraries/${tripData?._id}`
        : `/bookings/trips/${tripData?._id}`;
      
      // Build request body - only include batchId for trip bookings
      const requestBody: any = {
        "quantity": guests?.length,
        "userInfo": guests,
        "paymentId": razorpay_payment_id
      };
      
      // Only include batchId for trip type bookings
      if (Type === 'trip' && batchId?.id) {
        requestBody.batchId = batchId.id;
      }
      
      const res: any = await request<{ data:any, razorpayOrderId: string }>(endpoint, {
        method: 'POST',
        body: requestBody,
        authorization: true,
      });
      
      if (res?.success) {
        console.log("confirmBooking success", res);
        
        window.dispatchEvent(
          new CustomEvent('app-toast', {
            detail: {
              message: 'Booking confirmed successfully!',
              type: 'success',
            },
          }),
        );
        
        /**
         * STACK CLEAR REQUIRED HERE:
         * After successful booking/payment completion, we clear the navigation stack to:
         * 1. Prevent users from going back to the checkout form using browser back button
         * 2. Avoid accidental duplicate bookings/payments if user navigates back
         * 3. Ensure the payment form is not resubmitted accidentally
         * 4. Navigate back to the trip detail page to show booking confirmation
         * 5. Protect against potential security issues with cached payment data
         * 
         * Using replace: true instead of push ensures checkout page is removed from history
         */
        // Navigate back to the trip detail page after successful booking
        navigate(`/trips/${tripData?._id}?type=${Type}`, { 
          replace: true,
          state: { type: Type }
        });
      } else {
        console.error("confirmBooking failed", res);
        window.dispatchEvent(
          new CustomEvent('app-toast', {
            detail: {
              message: res?.message || 'Booking failed. Please try again or contact support.',
              type: 'error',
            },
          }),
        );
      }
    } catch (err: any) {
      console.error("confirmBooking error", err);
      window.dispatchEvent(
        new CustomEvent('app-toast', {
          detail: {
            message: err?.errorData?.message || err?.message || 'Booking failed. Please try again or contact support.',
            type: 'error',
          },
        }),
      );
    }
  }

  const handleAddGuest = () => {
    if (guests.length >= MAX_GUESTS) {
      window.dispatchEvent(
        new CustomEvent('app-toast', {
          detail: {
            message: `Maximum ${MAX_GUESTS} guests can be added`,
            type: 'error',
          },
        }),
      );
      return;
    }

    const maxGuestsAllowed = getAvailableSlots();

    if (guests.length < maxGuestsAllowed) {
      setGuests([...guests, { name: '', age: '', gender: '', contact: '' }]);
      return;
    }

    if (Type === 'trip') {
      window.dispatchEvent(
        new CustomEvent('app-toast', {
          detail: {
            message: maxGuestsAllowed > 0
              ? `Only ${maxGuestsAllowed} guest${maxGuestsAllowed > 1 ? 's are' : ' is'} allowed for this date`
              : 'No slots available for this date',
            type: 'error',
          },
        }),
      );
    }
  };

  const handleGuestChange = (index: number, field: keyof Guest, value: string) => {
    const newGuests = [...guests];
    newGuests[index] = { ...newGuests[index], [field]: value };
    setGuests(newGuests);
  };

  const handleDeleteGuest = (index: number) => {
    if (guests.length > 1) {
      const newGuests = guests.filter((_, i) => i !== index);
      setGuests(newGuests);
    }
  };

  // Extract appConfig from trip data
  const appConfig = tripData?.appConfig || tripData?.appconfig || {};



  const pricePerPerson = tripData?.pricePerPerson ;
  
  /** All amounts are integer paise; round after each % so UI lines sum to the total. */
  const roundPaise = (n: number) => Math.round(n);

  const gstPct = Number(appConfig.gst) || 0;
  const pgFeePct = Number(appConfig.paymentGatewayFee) || 0;
  const pgGstPct = Number(appConfig.paymentGatewayGst) || 0;

  // Standardized calculation system
  const totalcharges = pricePerPerson * guests.length ;

  // GST calculation (standard practice: GST on base amount)
  const gst = roundPaise(totalcharges * (gstPct / 100));

  const subtotal = totalcharges + gst;
  
  // Payment Gateway Fee (standard practice: fee on subtotal)
  const paymentGatewayFee = roundPaise(subtotal * (pgFeePct / 100));
  
  // Payment Gateway GST (standard practice: GST on gateway fee)
  const gstOnPaymentGatewayFee = roundPaise(paymentGatewayFee * (pgGstPct / 100));
  
  const totalTaxesAndCharges = gst + paymentGatewayFee + gstOnPaymentGatewayFee;
  
  // Final total (integer paise — matches sum of rounded line items)
  const totalPrice = subtotal + paymentGatewayFee + gstOnPaymentGatewayFee;
  
  // Helper function for consistent number formatting
  const formatCurrency = (amount: number) => `₹${amount.toFixed(2)}`;
  
  // Validation: Ensure all calculations are valid numbers
  const isValidCalculation = (amount: number) => !isNaN(amount) && isFinite(amount);
  
  // Validate all calculated amounts
  if (!isValidCalculation(totalcharges) || !isValidCalculation(totalPrice)) {
    console.error('Invalid calculation detected:', { totalcharges, totalPrice, pricePerPerson, guests: guests.length });
  }

  // Validate all guests: name, age, and gender are required for all guests
  // Contact is required only for guest 1 (index 0), optional for guests 2+
  const hasValidGuest = guests.every((guest, index) => {
    const hasRequiredFields = guest.name.trim() && guest.age && guest.gender;
    // Contact is required only for the first guest
    const hasValidContact = index === 0 ? guest.contact.trim() : true;
    return hasRequiredFields && hasValidContact;
  });

  // Helper function to check if a guest field is invalid
  const isGuestFieldInvalid = (index: number, field: keyof Guest) => {
    const guest = guests[index];
    if (field === 'name') return !guest.name.trim();
    if (field === 'age') return !guest.age;
    if (field === 'gender') return !guest.gender;
    if (field === 'contact') return index === 0 && !guest.contact.trim();
    return false;
  };

  // Check if batches are selected (only required for trip bookings, not itinerary)
  const hasValidBatch = Type === 'itinerary' || batchId !== null;
  const maxGuestsAllowed = getAvailableSlots();
  const canAddMoreGuests = guests.length < maxGuestsAllowed;

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', flexDirection: 'column', overflowX: 'hidden', width: '100%' }}>

      {/* Navigation Bar */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white' }}>
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton edge="start" onClick={handleBack} sx={{ color: '#000' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ color: '#000', ml: 1, fontWeight: 500 }}>
            Checkout
          </Typography>
        </Toolbar>
      </AppBar>

      <Box maxWidth="sm" sx={{ py: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden', width: '100%', maxWidth: '100%', boxSizing: 'border-box', mx: 'auto' }}>
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pb: 2, width: '100%', boxSizing: 'border-box' }}>
          {/* Trip Details */}   
          {Type === 'trip' &&
          <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', width: '100%', boxSizing: 'border-box' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Trip to {tripData?.title}
            </Typography>
            
            <Autocomplete
              fullWidth
              options={tripData.batches
                .filter((batch: any) => {
                  // Check if batch is not in the past (from date should be today or future)
                  const now = new Date();
                  now.setHours(0, 0, 0, 0);
                  const batchFromDate = new Date(batch.from);
                  batchFromDate.setHours(0, 0, 0, 0);
                  const isNotPast = batchFromDate >= now;

                  // Check if status is active
                  const isActive = batch.status === 'active' && batch.bookingPaused !== true ;
               

                  // Check if batch is not full (groupSize != maxGroupSizeAllowed)
                  const groupSize = batch.groupSize || 0;
                  const maxGroupSize = batch.maxGroupSizeAllowed || 0;
                  const isNotFull = groupSize !== maxGroupSize;

                  // Show batch only if it's not past, active, and not full
                  return isNotPast && isActive && isNotFull;
                })
                .map((batch: any) => ({
                  label: `${format(new Date(batch.from), "do MMMM yy")} - ${format(new Date(batch.to), "do MMMM yy")}`,
                  id: batch._id || batch.id,
                  batches: batch
                }))}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => <TextField {...params} label="Select trip date" />}
              value={batchId}
              onChange={(e, value) => {
                if (value) {
                  const selectedBatch = value.batches;
                  const groupSize = Number(selectedBatch?.groupSize) || 0;
                  const maxGroupSize = Number(selectedBatch?.maxGroupSizeAllowed) || 0;
                  const remainingSlots = Math.max(maxGroupSize - groupSize, 0);
                  const nextMaxGuestsAllowed = Math.min(5, remainingSlots);

                  setBatchId({
                    label: value.label,
                    id: value.id,
                    batches: value.batches
                  });

                  if (nextMaxGuestsAllowed > 0 && guests.length > nextMaxGuestsAllowed) {
                    setGuests((prevGuests) => prevGuests.slice(0, nextMaxGuestsAllowed));
                    window.dispatchEvent(
                      new CustomEvent('app-toast', {
                        detail: {
                          message: `Guest count adjusted to ${nextMaxGuestsAllowed} based on available slots`,
                          type: 'error',
                        },
                      }),
                    );
                  }

                  console.log("Selected batches:", value);
                } else {
                  setBatchId(null);
                }
              }}
              sx={{ mt: 1 }}  
              />
            {Type === 'trip' && batchId === null && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                Please select a trip date to continue
              </Typography>
            )}
          </Box>
          } 

          {/* Personal Details */}
          <Box 
            sx={{ 
              mb: 2, 
              border: '2px  #F57C00',
              borderTopLeftRadius: '32px',
              borderTopRightRadius: '32px',
              borderBottomLeftRadius: 3,
              borderBottomRightRadius: 3,
              backgroundColor: '#FFF3E0',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Box sx={{ p: 3, pb: 2, flexShrink: 0 }}>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
                Personal details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Type === 'trip' ? 'Max of 5 guest can be added for this trip' : 'Enter the details for the itinerary purchase'}
              </Typography>
            </Box>
            <Box sx={{ overflowY: 'auto', overflowX: 'hidden', px: 3, pb: 3, width: '100%', boxSizing: 'border-box' }}>

            {guests.map((guest, index) => (
              <Box key={index}>
                {index > 0 && (
                  <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />
                )}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    borderRadius: 2,
                    bgcolor: index === 0 ? 'transparent' : '#fafafa',
                    border: index === 0 ? 'none' : '1px solid #f0f0f0',
                    transition: 'all 0.2s ease',
                    width: '100%',
                    boxSizing: 'border-box',
                    '&:hover': {
                      bgcolor: index === 0 ? 'transparent' : '#f5f5f5',
                    }
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                    {Type === 'trip' &&   
                      <Typography 
                        variant="subtitle1" 
                        fontWeight="600" 
                        sx={{ 
                          color: COLORS.primary,
                          fontSize: '1rem'
                        }}
                      >
                        Guest {index + 1}
                      </Typography>
                    }
                    {Type !== 'trip' && index === 0 && (
                      <Typography 
                        variant="subtitle1" 
                        fontWeight="600" 
                        sx={{ 
                          color: COLORS.primary,
                          fontSize: '1rem'
                        }}
                      >
                        Your Details
                      </Typography>
                    )}
                    {index > 0 && (
                      <IconButton
                        onClick={() => handleDeleteGuest(index)}
                        sx={{ 
                          color: 'error.main',
                          bgcolor: 'rgba(211, 47, 47, 0.08)',
                          '&:hover': {
                            bgcolor: 'rgba(211, 47, 47, 0.15)',
                          }
                        }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                  
                  <TextField
                    fullWidth
                    label="Enter Name"
                    value={guest.name}
                    onChange={(e) => handleGuestChange(index, 'name', e.target.value)}
                    error={isGuestFieldInvalid(index, 'name')}
                    helperText={isGuestFieldInvalid(index, 'name') ? 'Name is required' : ''}
                    required
                    sx={{ 
                      mb: 2.5,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: 'white',
                      }
                    }}
                  />
                  
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    spacing={2} 
                    sx={{ mb: 2.5, width: '100%' }}
                  >
                    <Box sx={{ width: { xs: '100%', sm: '50%' }, flexShrink: 0 }}>
                      <Autocomplete
                        fullWidth
                        options={Array.from({ length: 100 }, (_, i) => i + 1).map((age) => age.toString())}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Age"
                            error={isGuestFieldInvalid(index, 'age')}
                            helperText={isGuestFieldInvalid(index, 'age') ? 'Age is required' : ''}
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                bgcolor: 'white',
                              }
                            }}
                          />
                        )}
                        value={guest.age}
                        onChange={(e, value) => handleGuestChange(index, 'age', value || '')}
                      />
                    </Box>
                    
                    <Box sx={{ width: { xs: '100%', sm: '50%' }, flexShrink: 0 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
                        <Button
                          variant={guest.gender === 'male' ? 'contained' : 'outlined'}
                          onClick={() => handleGuestChange(index, 'gender', 'male')}
                          sx={{
                            flex: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            py: 1.25,
                            bgcolor: guest.gender === 'male' ? COLORS.primary : 'transparent',
                            color: guest.gender === 'male' ? 'white' : COLORS.primary,
                            borderColor: isGuestFieldInvalid(index, 'gender') ? 'error.main' : COLORS.primary,
                            borderWidth: 1.5,
                            '&:hover': {
                              bgcolor: guest.gender === 'male' ? COLORS.burntOrange : 'rgba(255, 107, 53, 0.08)',
                              borderColor: COLORS.primary,
                            }
                          }}
                        >
                          Male
                        </Button>
                        <Button
                          variant={guest.gender === 'female' ? 'contained' : 'outlined'}
                          onClick={() => handleGuestChange(index, 'gender', 'female')}
                          sx={{
                            flex: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            py: 1.25,
                            bgcolor: guest.gender === 'female' ? COLORS.primary : 'transparent',
                            color: guest.gender === 'female' ? 'white' : COLORS.primary,
                            borderColor: isGuestFieldInvalid(index, 'gender') ? 'error.main' : COLORS.primary,
                            borderWidth: 1.5,
                            '&:hover': {
                              bgcolor: guest.gender === 'female' ? COLORS.burntOrange : 'rgba(255, 107, 53, 0.08)',
                              borderColor: COLORS.primary,
                            }
                          }}
                        >
                          Female
                        </Button>
                      </Box>
                      {isGuestFieldInvalid(index, 'gender') && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                          Gender is required
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  
                  <TextField
                    fullWidth
                    label={index === 0 ? "Contact no." : "Contact no. (Optional)"}
                    value={guest.contact}
                    onChange={(e) => handleGuestChange(index, 'contact', e.target.value)}
                    error={isGuestFieldInvalid(index, 'contact')}
                    helperText={isGuestFieldInvalid(index, 'contact') ? 'Contact number is required' : ''}
                    required={index === 0}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: 'white',
                      }
                    }}
                  />
                </Box>
              </Box>
            ))}

            {Type === 'trip' && (
              <Button
                variant="text"
                onClick={handleAddGuest}
                disabled={!batchId || !canAddMoreGuests}
                sx={{
                  color: COLORS.primary, textTransform:'none'
                }}
                // sx={{ 
                //   mt: 2,
                //   color: COLORS.primary, 
                //   borderColor: COLORS.primary,
                //   textTransform: 'none',
                //   borderRadius: 2,
                //   py: 1.25,
                //   fontWeight: 500,
                //   borderWidth: 1.5,
                //   '&:hover': {
                //     borderColor: COLORS.primary,
                //     bgcolor: 'rgba(255, 107, 53, 0.08)',
                //     borderWidth: 1.5,
                //   }
                // }}
                // fullWidth
              >
                + Add guest
              </Button>
            )}
            {Type === 'trip' && !batchId && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                Select trip date first to add guests
              </Typography>
            )}
            {Type === 'trip' && batchId?.batches && !canAddMoreGuests && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {maxGuestsAllowed > 0
                  ? `Only ${maxGuestsAllowed} guest${maxGuestsAllowed > 1 ? 's' : ''} can be added for this date`
                  : 'No slots available for this date'}
              </Typography>
            )}
            </Box>
          </Box>
        </Box>

        {/* Fixed Bottom Section - Price Details and Payment Button */}
        <Box sx={{ mt: 'auto', pt: 2 }}>
          {/* Price Details */}
          <Box 
            sx={{ 
              p: 2, 
              mb: 2, 
              borderRadius: 2, 
              bgcolor: '#FFF3E0',
              cursor: 'pointer'
            }}
            onClick={() => setShowPriceDetails(!showPriceDetails)}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <DescriptionIcon sx={{ color: COLORS.primary }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  To Pay {paiseToRupees(totalPrice)}             </Typography>
                <Typography variant="body2" color="text.secondary">
                  {gst > 0 ? `Incl. GST, PG fees & charges` : 'Incl. all taxes & charges'}
                </Typography>
              </Box>
              {showPriceDetails ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </Stack>
            
            {showPriceDetails && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                {/* Base Price Breakdown */}
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2">Base Price</Typography>
                  <Typography variant="body2">
                    {Type === 'trip' 
                      ? `${paiseToRupees(pricePerPerson)} × ${guests.length} guests`
                      : paiseToRupees(pricePerPerson)
                    }
                  </Typography>
                </Stack>
                {/* Taxes and Charges Breakdown */}
                {gstPct > 0 && (
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="body2">GST ({gstPct}%)</Typography>
                    <Typography variant="body2">{paiseToRupees(gst)}</Typography>
                  </Stack>
                )}
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2">Subtotal</Typography>
                  <Typography variant="body2">{paiseToRupees(subtotal)}</Typography>
                </Stack>
                {pgFeePct > 0 && (
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="body2">Payment Gateway Fee ({pgFeePct}%)</Typography>
                    <Typography variant="body2">{paiseToRupees(paymentGatewayFee)}</Typography>
                  </Stack>
                )}
                {appConfig.paymentGatewayGst > 0 && (
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="body2">PG GST ({appConfig.paymentGatewayGst}%)</Typography>
                    <Typography variant="body2">{paiseToRupees(gstOnPaymentGatewayFee)}</Typography>
                  </Stack>
                )}
                
                {/* Summary */}
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2">Total Taxes & Charges</Typography>
                  <Typography variant="body2">{paiseToRupees(totalTaxesAndCharges)}</Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h6" fontWeight="bold">Total Amount</Typography>
                  <Typography variant="h6" fontWeight="bold">{paiseToRupees(totalPrice)}</Typography>
                </Stack>
              </Box>
            )}
          </Box>
          <PaymentButton
            amount={paiseToRupees(totalPrice)}
            currency="INR"
            paymentFor={Type === 'trip'? "trip_booking":'itinerary_purchase'}
            referenceId={tripData?._id}
            userData={{
              name: guests[0].name,
              // email: guests[0].email || '',
              contact: guests[0].contact,
            }}
            onPaymentSuccess={(razorpay_payment_id) => {
              confirmBooking(razorpay_payment_id);
            }}
            onPaymentError={() => {}}
            buttonText="Pay Now"
            disabled={!hasValidGuest || !hasValidBatch}
            quantity={guests.length}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Checkout;
